import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import NavbarSidebar from '../layout/NavbarSidebar';

const FloatingSidebar = ({ isExpanded, onToggleExpand }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [showIndicator, setShowIndicator] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const sidebarRef = useRef(null);
  const triggerRef = useRef(null);
  const timeoutRef = useRef(null);

  // Detect mobile devices
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    // Check initial
    checkMobile();
    
    // Add event listener
    window.addEventListener('resize', checkMobile);
    
    // Clean up
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleShow = () => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    setIsVisible(true);
    setShowIndicator(false);
  };

  const handleHide = () => {
    // Don't hide if we're hovering the sidebar
    if (sidebarRef.current && sidebarRef.current.contains(document.activeElement)) {
      return;
    }

    // Add a delay before hiding to prevent flickering and allow moving to the sidebar
    timeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      
      // Show the indicator again after the sidebar is hidden
      setTimeout(() => {
        setShowIndicator(true);
      }, 500);
    }, 600);
  };

  // Initially show the indicator with a delay after component mounts
  useEffect(() => {
    if (isExpanded) {
      const timer = setTimeout(() => {
        setShowIndicator(true);
      }, 1500);
      return () => clearTimeout(timer);
    }
  }, [isExpanded]);

  // Clean up timeout on unmount
  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  // Handle when the mouse moves over the document
  useEffect(() => {
    if (!isExpanded) return;

    const handleMouseMove = (e) => {
      // Show sidebar when mouse is near left edge
      if (e.clientX < 40 && !isVisible) {
        handleShow();
      } 
      // Hide sidebar when mouse is far from left edge and sidebar
      else if (isVisible && e.clientX > 320 && (!sidebarRef.current || !sidebarRef.current.contains(e.target))) {
        handleHide();
      }
    };

    // Don't use mouse events on mobile
    if (!isMobile) {
      document.addEventListener('mousemove', handleMouseMove);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
    };
  }, [isExpanded, isVisible, isMobile]);

  // Only show when expanded
  if (!isExpanded) return null;

  const handleExpandClick = () => {
    if (onToggleExpand) {
      // Hide the sidebar first
      setIsVisible(false);
      // Then toggle expand after a short delay
      setTimeout(() => {
        onToggleExpand();
      }, 300);
    }
  };

  return (
    <>
      {/* Visual indicator for the sidebar */}
      <AnimatePresence>
        {showIndicator && (
          <motion.div 
            className="fixed top-1/2 left-0 w-3 h-24 bg-orange-400/80 rounded-r-md -translate-y-1/2 z-[9999] cursor-pointer"
            initial={{ opacity: 0 }}
            animate={{ opacity: 0.8 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            whileHover={{ opacity: 1, width: 5 }}
            onClick={handleShow}
            ref={triggerRef}
          />
        )}
      </AnimatePresence>

      {/* Floating sidebar with glassmorphism */}
      <AnimatePresence>
        {isVisible && (
          <motion.div
            ref={sidebarRef}
            className="fixed top-0 left-0 h-full z-[10000] bg-white/40 backdrop-blur-xl shadow-lg rounded-r-2xl overflow-hidden border-r border-orange-100/50"
            initial={{ x: -300, opacity: 0.5 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: -300, opacity: 0 }}
            transition={{ type: "spring", stiffness: 300, damping: 30 }}
            style={{ width: isMobile ? "85%" : "288px", maxWidth: "350px" }}
            onMouseLeave={(e) => {
              // Only trigger leave if moving to the right and not on mobile
              if (!isMobile && e.clientX > (isMobile ? window.innerWidth * 0.85 : 288)) {
                handleHide();
              }
            }}
          >
            <div className="h-full bg-gradient-to-br from-white/30 to-orange-50/30 backdrop-blur-md flex flex-col">
              {/* Close button for mobile - more prominent at the top */}
              {isMobile && (
                <div className="flex items-center justify-between p-4 border-b border-orange-100/30">
                  <h2 className="text-orange-700 font-medium">EOS Yes</h2>
                  <button
                    onClick={() => setIsVisible(false)}
                    className="p-2 rounded-full bg-orange-100/50 text-orange-700 hover:bg-orange-100/70 hover:text-orange-600 transition-all duration-300"
                    aria-label="Close Sidebar"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              )}

              {/* Main content - NavbarSidebar */}
              <div className="flex-1 overflow-auto scrollbar-thin">
                <NavbarSidebar 
                  isExpanded={false} 
                  hideTitle={true}
                  isMobile={isMobile}
                  onCloseMobile={() => setIsVisible(false)}
                />
              </div>
              
              {/* Minimize button */}
              <div className="px-3 mb-4">
                <button
                  onClick={handleExpandClick}
                  className="w-full py-2.5 px-4 bg-orange-50/60 hover:bg-orange-100/70 text-orange-700 rounded-xl transition-all duration-300 flex items-center justify-center font-medium text-sm"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M8 3v3a2 2 0 0 1-2 2H3"></path>
                    <path d="M21 8V5a2 2 0 0 0-2-2h-3"></path>
                    <path d="M3 16v3a2 2 0 0 0 2 2h3"></path>
                    <path d="M16 21h3a2 2 0 0 0 2-2v-3"></path>
                  </svg>
                  {isMobile ? "Exit Fullscreen" : "Minimize Window"}
                </button>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Mobile-friendly swipe indicator */}
      {isMobile && isExpanded && !isVisible && (
        <div 
          className="fixed left-0 top-0 h-full w-12 z-[9998]"
          onTouchStart={handleShow}
        />
      )}
    </>
  );
};

export default FloatingSidebar; 